import React, { useState, useEffect, useRef } from 'react';
import Slide from 'react-reveal/Slide';
import { TProductFeatureComponentProps, TComponentDetails } from '../types/productFeatureComponent.types';
import ComponentDetails from './componentDetails';
import { ProductFeatureModel } from '../models/productFeatureComponent.model';
import FeatureHeader from './featureHeader';
import useScrollWithShadow from '../../../../hooks/useScrollWithShadow';
import { windowService } from '../../../../utils/window.utils';

const DesktopSeriesDetails = (props: TProductFeatureComponentProps): JSX.Element => {
  const model = new ProductFeatureModel(props);
  const isEEActive = props?.sitecoreContext?.pageEditing;
  const [selectedComponent, setSelectedComponent] = useState<TComponentDetails[]>([]);
  const { showShadowTop, showShadowBottom, scrollElementRef, onScrollHandler } = useScrollWithShadow();
  const [shadowWidth, setShadowWidth] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    function handleResize() {
      const newShadowWidth = scrollElementRef?.current?.clientWidth || 0;
      setShadowWidth(newShadowWidth);
    }

    handleResize();
    windowService().addEventListener('resize', handleResize);
    return (): void => windowService().removeEventListener('resize', handleResize);
  }, [scrollElementRef]);

  const setImageWidth = () => {
    if (sectionRef?.current) {
      sectionRef?.current?.style.setProperty('--pfe-feature-image-Width', `${sectionRef?.current?.clientWidth}px`);
    }
  };

  useEffect(() => {
    if (model?.innerComponents?.length && model?.innerComponents[0]) {
      setSelectedComponent([model?.innerComponents[0]]);
    }
  }, [model.contextItemUrl]);

  useEffect(() => {
    setImageWidth();
  }, [sectionRef]);

  useEffect(() => {
    windowService()?.addEventListener('resize', setImageWidth);
    return () => {
      windowService()?.removeEventListener('resize', setImageWidth);
    };
  }, []);

  const automaticScrollTop = (event): void => {
    event.target?.parentElement?.scrollBy({
      top: -50,
      left: 0,
      behavior: 'smooth',
    });
  };
  const automaticScrollBottom = (event): void => {
    event.target?.parentElement?.scrollBy({
      top: 50,
      left: 0,
      behavior: 'smooth',
    });
  };

  return (
    <>
      <section className="product-feature product-white-spacer">
        <div className="product-feature__container">
          <div className="product-feature__tabs-wrapper" onScroll={onScrollHandler} ref={scrollElementRef}>
            {showShadowTop && (
              <div
                className="product-feature__shadow product-feature__shadow-top"
                style={{ width: shadowWidth }}
                onClick={automaticScrollTop}
              ></div>
            )}
            {showShadowBottom && (
              <div
                className="product-feature__shadow product-feature__shadow-bottom"
                style={{ width: shadowWidth }}
                onClick={automaticScrollBottom}
              ></div>
            )}
            <FeatureHeader
              title={model?.title}
              isEEActive={isEEActive}
              titleDictionaryKeyFeature={props.titleDictionaryKeyFeature}
              rendering={props.rendering}
            />
            <ul className="product-feature__tabs">
              {model?.innerComponents?.map((component) => (
                <li
                  className={`product-feature__tab ${
                    selectedComponent[selectedComponent.length - 1]?.id === component?.id ? 'product-feature__tab--selected' : ''
                  }`}
                  key={`${component?.id} `}
                >
                  <button
                    className={`product-feature__tab-button ${
                      selectedComponent[selectedComponent.length - 1]?.id === component.id ? 'product-feature__tab-button--selected' : ''
                    }`}
                    onClick={() => {
                      setSelectedComponent((data) => {
                        if (component.id !== data?.[data?.length - 1]?.id) {
                          data.push(component);
                        }
                        if (data.length >= 3) {
                          return [...data.slice(1)];
                        }
                        return [...data];
                      });
                    }}
                  >
                    <h3>{component.eyebrow}</h3>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <section className="product-feature__component-section" ref={sectionRef}>
            {selectedComponent && (
              <Slide bottom duration={500} key={selectedComponent[selectedComponent?.length - 1]?.id}>
                <div className="product-feature__component-wrapper">
                  <ComponentDetails
                    component={selectedComponent[selectedComponent.length - 1]}
                    isSeries={props?.isSeries}
                    cardCTAVisibleEvent={props?.cardCTAVisibleEvent}
                    className="product-feature__component"
                  />
                </div>
              </Slide>
            )}
          </section>
        </div>
      </section>
    </>
  );
};

export default DesktopSeriesDetails;
